import React from 'react'
import { Helmet } from 'react-helmet-async'
import { SITE_NAME, SITE_SHORT_NAME, TITLE_MAX, absUrl } from '@/config/site'

interface SEOHeadProps {
  title: string
  description: string
  keywords?: string
  canonicalUrl?: string
  ogImage?: string
  ogType?: 'website' | 'article'
  noIndex?: boolean
  structuredData?: object
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = '/og-image.jpg',
  ogType = 'website',
  noIndex = false,
  structuredData
}) => {
  // Marka eki yalnızca 60 karakter sınırını aşmıyorsa eklenir; aşıyorsa
  // başlığın anahtar kelime taşıyan kısmı korunur.
  const suffix = ` | ${SITE_SHORT_NAME}`
  const fullTitle = title.includes('Ardıç Veteriner')
    ? title
    : title.length + suffix.length <= TITLE_MAX
      ? `${title}${suffix}`
      : title
  // Sosyal ağ botları göreli yolu çözemez; her zaman mutlak URL gönderilir.
  // WhatsApp ve Facebook WebP önizlemeyi güvenilir işlemediğinden,
  // WebP verilen durumda JPG olan varsayılan paylaşım görseline düşülür.
  const shareImage = ogImage.endsWith('.webp') ? '/og-image.jpg' : ogImage
  const absoluteImage = absUrl(shareImage)
  const absoluteCanonical = canonicalUrl ? absUrl(canonicalUrl) : undefined

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Canonical URL */}
      {absoluteCanonical && <link rel="canonical" href={absoluteCanonical} />}

      {/* Robots */}
      <meta
        name="robots"
        content={noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1'}
      />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      {absoluteCanonical && <meta property="og:url" content={absoluteCanonical} />}
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="tr_TR" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  )
}

export default SEOHead
